import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { useState, useEffect } from "react";
import axios from "@/utils/axios";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FiUser } from "react-icons/fi";
import { PiUserCircleDashed } from "react-icons/pi";
import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";

import { toast } from 'sonner';

export function AssigneeSelect({ task, organizationSlug, showName = false, onAssigneeChange, onTaskChange, onRefreshActivities }) {
    const [open, setOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const { axiosInstance } = useAuth();
    const { showLoading, hideLoading } = useGlobalLoading();

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                params.append('organizationSlug', organizationSlug);
                if (task?.key) {
                    params.append('taskKey', task.key);
                }
                if (task?.project?.id) {
                    params.append('projectId', task.project.id);
                }
                const response = await axiosInstance.get(`/users/sorted?${params.toString()}`);
                setUsers(response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };

        if (open && organizationSlug) {
            fetchUsers();
        }
    }, [open, organizationSlug, task?.key, task?.project?.id, axiosInstance]);

    // Get the current assignee's ID or "unassigned" as the selected value
    const selectedValue = task.assignee?.id || "unassigned";

    const handleAssigneeChange = async (newAssignee) => {
        // 1. Capture old value for rollback
        const oldAssignee = task.assignee;
        
        // 2. Optimistically update UI immediately
        const optimisticTask = {
            ...task,
            assignee: newAssignee ? {
                id: newAssignee.id,
                name: newAssignee.name,
                initials: newAssignee.initials,
                avatarColor: newAssignee.avatarColor
            } : null
        };
        
        if (onTaskChange) {
            onTaskChange(optimisticTask);
        }
        
        if (onAssigneeChange) {
            onAssigneeChange(newAssignee);
        }

        // 3. Fire API call (don't await before UI update)
        try {
            showLoading();
            await axiosInstance.patch(`/tasks/${task.organization.slug}/${task.key}`, {
                assigneeId: newAssignee?.id || null
            });
            
            // 4. Refresh just activities (new activity was created)
            if (onRefreshActivities) {
                await onRefreshActivities();
            }
        } catch (error) {
            // 5. Rollback on error
            console.error('Error updating assignee:', error);
            if (onTaskChange) {
                onTaskChange({ ...task, assignee: oldAssignee });
            }
            if (onAssigneeChange) {
                onAssigneeChange(oldAssignee);
            }
            toast({
                title: "Error",
                description: "Failed to update assignee",
                variant: "destructive"
            });
        } finally {
            hideLoading();
        }
    };

    return (
        <div 
            onClick={(e) => e.stopPropagation()} 
            className="inline-flex items-center m-0 p-0 leading-none"
        >
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className={`${showName ? "h-7 px-1.5 -ml-1.5 flex items-center gap-2" : "h-4 w-4 p-0 rounded-full bg-none hover:opacity-50"}`}>
                        {task.assignee ? (
                            <>
                                <Avatar className="h-4 w-4">
                                    <AvatarImage src={task.assignee.avatar} />
                                    <AvatarFallback className={`${task.assignee?.avatarColor || 'bg-neutral-600'} text-neutral-200 text-[0.65rem]`}>
                                        {task.assignee.initials}
                                    </AvatarFallback>
                                </Avatar>
                                {showName && <span>{task.assignee.name}</span>}
                            </>
                        ) : (
                            <>
                                <Avatar className="h-4 w-4">
                                    <PiUserCircleDashed className="text-neutral-500 h-4 w-4" />
                                </Avatar>
                                {showName && <span>Unassigned</span>}
                            </>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0" side="top" align="end">
                    <Command defaultValue={selectedValue}>
                        <CommandInput placeholder="Assign to..." />
                        <CommandList>
                            <CommandEmpty>No users found.</CommandEmpty>
                            <CommandGroup>
                                <CommandItem
                                    value="unassigned"
                                    onSelect={() => {
                                        handleAssigneeChange(null);
                                        setOpen(false);
                                    }}
                                >
                                    <div className="flex items-center gap-2">
                                        <Avatar className="h-5 w-5">
                                            <PiUserCircleDashed className="text-neutral-500 h-5 w-5" />
                                        </Avatar>
                                        <span>Unassigned</span>
                                    </div>
                                    {!task.assignee && (
                                        <Check className="ml-auto h-4 w-4" />
                                    )}
                                </CommandItem>
                                {users.map((user) => (
                                    <CommandItem
                                        key={user.id}
                                        value={`${user.name} ${user.email || ''}`.trim()}
                                        onSelect={() => {
                                            handleAssigneeChange(user);
                                            setOpen(false);
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Avatar className="h-5 w-5">
                                                <AvatarImage src={user.avatar} />
                                                <AvatarFallback className={`${user.avatarColor || 'bg-neutral-600'} text-neutral-200 text-[0.65rem]`}>
                                                    {user.initials}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span>{user.name}</span>
                                        </div>
                                        {task.assignee?.id === user.id && (
                                            <Check className="ml-auto h-4 w-4" />
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}